import { PoolTokenType, Networks, WETH, Amms, ADD_LIQUIDITY_URLS, SOLIDEX_FACTORIES, Factory } from 'impermax-sdk';
import { useWallet } from 'use-wallet';

import useFactory from './useFactory';
import useGenericCall from './useGenericCall';
import { useNetworkName } from './useNetwork';
import { useOffchainLendingPool, useOffchainPoolToken } from './useOffchain';
import { useOnchainPoolToken } from './useOnchain';
import usePairAddress from './usePairAddress';

require("../assets/default.png");

export function useLendingPoolUrl(): string {
    const factory = useFactory();
    const pairAddress = usePairAddress();
    return '/lending-pool/' + factory + '/' + pairAddress;
}

export function useThisAccountUrl(): string {
    const { account } = useWallet();
    if (!account) return null;
    return '/account/' + account;
}

export function useTokenIcon(poolTokenTypeArg?: PoolTokenType): string {
    const tokenAddress = useGenericCall(useOffchainPoolToken(poolTokenTypeArg), (s) => s.getUnderlyingAddress()) ?? '';
    const convertedAddress = tokenAddress.toLowerCase();
    try {
        require(`../assets/icons/${convertedAddress}.png`);
        return `/images/${convertedAddress}.png`;
    } catch {
        return '/images/default.png';
    }
}

export function useAddLiquidityUrl(): string {
    const network = useNetworkName();
    const ETH_STRING = network === Networks.Avalanche ? 'AVAX' : 'ETH';
    const factory = useFactory();
    const amm = useGenericCall(useOffchainLendingPool(), (s) => s.getAmm());

    if (SOLIDEX_FACTORIES.includes(factory)) {
        const lpAddress =
            useGenericCall(useOnchainPoolToken(PoolTokenType.Collateral), (r) => r.getTokenAddress()) ?? '';
        return ADD_LIQUIDITY_URLS[network][amm] + lpAddress.toLowerCase();
    } else {
        const addresses = [PoolTokenType.BorrowableA, PoolTokenType.BorrowableB].map((borrowable) =>
            useGenericCall(useOffchainPoolToken(borrowable), async (s) => {
                const tokenAddress = await s.getUnderlyingAddress();
                return tokenAddress.toLowerCase() == WETH[network].toLowerCase() ? ETH_STRING : tokenAddress;
            })
        );
        if (amm === Amms.solidly) return ADD_LIQUIDITY_URLS[network][amm];
        const liquidityUrl = ADD_LIQUIDITY_URLS[network][amm] + addresses[0] + '/' + addresses[1];
        return network == Networks.Base && factory == Factory.V2V2 ? liquidityUrl + "?chain=base" : liquidityUrl;
    }
}

export function useTransactionUrlGenerator(): (hash: string) => string {
    const network = useNetworkName();
    const scanUrl =
        network === Networks.Mainnet
            ? 'etherscan.io'
            : network === Networks.Ropsten
            ? 'ropsten.etherscan.io'
            : network === Networks.Polygon
            ? 'polygonscan.com'
            : network === Networks.Arbitrum
            ? 'arbiscan.io'
            : network === Networks.Moonriver
            ? 'moonriver.moonscan.io'
            : network === Networks.Fantom
            ? 'ftmscan.com'
            : network === Networks.Canto
            ? 'tuber.build'
            : network === Networks.ZksyncEra
            ? 'explorer.zksync.io'
            : network === Networks.Avalanche
            ? 'snowtrace.io'
            : network === Networks.Mantle
            ? 'explorer.mantle.xyz'
            : network === Networks.Scroll
            ? 'scrollscan.com'
            : network === Networks.Real
            ? 'explorer.re.al'
            : network === Networks.Base
            ? 'basescan.org'
            : network === Networks.Blast
            ? 'blastscan.io'
            : network === Networks.Optimism
            ? 'optimistic.etherscan.io'
            : network === Networks.Sonic
            ? 'sonicscan.org'
            : network === Networks.Linea
            ? 'lineascan.build'
            : '';
    return (hash: string) => 'https://' + scanUrl + '/tx/' + hash;
}

export function useTransactionUrl(hash: string): string {
    const generator = useTransactionUrlGenerator();
    return generator(hash);
}
